import { useEffect, useMemo, useState } from "react";
import {
  salesOfferTypeOptions,
  salesToneOptions,
  salesTrafficSourceOptions,
} from "@/features/tools/salesHeadlineOptions";
import type { Project } from "@/features/projects/projectTypes";
import type {
  Blueprint,
  SalesHeadlineOfferType,
  SalesHeadlineRequest,
  SalesHeadlineTone,
  SalesHeadlineTrafficSource,
} from "@/features/tools/proToolTypes";

type GeneratedBlueprintOption = Pick<
  Blueprint,
  "id" | "project_id" | "title" | "target_builder" | "created_at" | "updated_at"
>;

type SalesHeadlineFormProps = {
  projects: Project[];
  generatedBlueprints: GeneratedBlueprintOption[];
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (input: SalesHeadlineRequest) => void;
};

type InputMode = "blueprint" | "manual";

const MANUAL_DESCRIPTION_MIN_LENGTH = 40;
const MANUAL_DESCRIPTION_HELPER =
  "Describe what the app does, who it helps, and the problem it solves.";
const MANUAL_DESCRIPTION_ERROR =
  "Please add a bit more detail so the generator can create useful headline options.";
const MANUAL_DESCRIPTION_EXAMPLE =
  "Example: A simple app that helps coaches track client goals, weekly actions, progress notes, and next steps.";

const vagueManualDescriptions = new Set([
  "app",
  "an app",
  "just app",
  "simple app",
  "my app",
  "test",
  "test app",
  "app idea",
  "build app",
  "something",
]);

function getInitialMode(blueprints: GeneratedBlueprintOption[]): InputMode {
  return blueprints.length > 0 ? "blueprint" : "manual";
}

function getInitialProjectId(
  projects: Project[],
  blueprints: GeneratedBlueprintOption[],
) {
  return blueprints[0]?.project_id ?? projects[0]?.id ?? "";
}

function formatBuilder(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isManualDescriptionMeaningful(description: string) {
  const normalized = description.trim().toLowerCase().replace(/\s+/g, " ");

  return (
    normalized.length >= MANUAL_DESCRIPTION_MIN_LENGTH &&
    !vagueManualDescriptions.has(normalized)
  );
}

export function SalesHeadlineForm({
  projects,
  generatedBlueprints,
  isSubmitting,
  error,
  onSubmit,
}: SalesHeadlineFormProps) {
  const [inputMode, setInputMode] = useState<InputMode>(() =>
    getInitialMode(generatedBlueprints),
  );
  const [hasChosenMode, setHasChosenMode] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(() =>
    getInitialProjectId(projects, generatedBlueprints),
  );
  const [selectedBlueprintId, setSelectedBlueprintId] = useState("");
  const [appDescription, setAppDescription] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [offerType, setOfferType] = useState<SalesHeadlineOfferType>("not_sure");
  const [tone, setTone] = useState<SalesHeadlineTone>("direct");
  const [trafficSource, setTrafficSource] =
    useState<SalesHeadlineTrafficSource>("unknown");
  const [extraContext, setExtraContext] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredBlueprints = useMemo(
    () =>
      generatedBlueprints.filter(
        (blueprint) => blueprint.project_id === selectedProjectId,
      ),
    [generatedBlueprints, selectedProjectId],
  );

  useEffect(() => {
    if (!hasChosenMode && inputMode === "manual" && generatedBlueprints.length > 0) {
      setInputMode("blueprint");
      return;
    }

    if (
      !selectedProjectId &&
      (projects.length > 0 || generatedBlueprints.length > 0)
    ) {
      setSelectedProjectId(getInitialProjectId(projects, generatedBlueprints));
    }
  }, [
    generatedBlueprints,
    hasChosenMode,
    inputMode,
    projects,
    selectedProjectId,
  ]);

  useEffect(() => {
    if (inputMode !== "blueprint") {
      return;
    }

    const selectedStillAvailable = filteredBlueprints.some(
      (blueprint) => blueprint.id === selectedBlueprintId,
    );

    if (!selectedStillAvailable) {
      setSelectedBlueprintId(filteredBlueprints[0]?.id ?? "");
    }
  }, [filteredBlueprints, inputMode, selectedBlueprintId]);

  const selectedBlueprint = useMemo(
    () =>
      filteredBlueprints.find(
        (blueprint) => blueprint.id === selectedBlueprintId,
      ),
    [filteredBlueprints, selectedBlueprintId],
  );

  const canSubmit = useMemo(() => {
    if (inputMode === "blueprint") {
      return Boolean(selectedBlueprint);
    }

    return isManualDescriptionMeaningful(appDescription);
  }, [appDescription, inputMode, selectedBlueprint]);

  const manualDescriptionState = useMemo(() => {
    if (inputMode !== "manual") {
      return null;
    }

    if (!appDescription.trim()) {
      return {
        tone: "helper" as const,
        message: MANUAL_DESCRIPTION_HELPER,
      };
    }

    if (!isManualDescriptionMeaningful(appDescription)) {
      return {
        tone: "error" as const,
        message: MANUAL_DESCRIPTION_ERROR,
      };
    }

    return {
      tone: "helper" as const,
      message: "This is enough detail to generate headline angles.",
    };
  }, [appDescription, inputMode]);

  function handleSubmit() {
    setLocalError(null);

    if (!canSubmit) {
      setLocalError(
        inputMode === "manual"
          ? MANUAL_DESCRIPTION_ERROR
          : "Select a generated blueprint before generating headline angles.",
      );
      return;
    }

    onSubmit({
      blueprintId: inputMode === "blueprint" ? selectedBlueprint?.id : undefined,
      projectId:
        inputMode === "blueprint" ? selectedBlueprint?.project_id : undefined,
      appDescription,
      targetAudience,
      desiredOutcome,
      offerType,
      tone,
      trafficSource,
      extraContext,
    });
  }

  return (
    <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div>
        <p className="text-sm font-semibold text-indigo-700">
          Sales Page Headline Generator
        </p>
        <h3 className="mt-2 text-xl font-semibold text-slate-950">
          Generate sales headline angles for this app
        </h3>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Select a generated blueprint, add audience or offer context, and get
          headline options across proven sales angles.
        </p>
      </div>

      {error || localError ? (
        <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {localError ?? error}
        </div>
      ) : null}

      <div className="mt-6">
        <div className="flex w-full flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-1 sm:w-auto">
          {(
            [
              ["blueprint", "Use generated blueprint"],
              ["manual", "Describe manually"],
            ] as Array<[InputMode, string]>
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setInputMode(mode);
                setHasChosenMode(true);
                setLocalError(null);
              }}
              className={`flex-1 whitespace-normal rounded-md px-3 py-2 text-sm font-semibold transition sm:flex-none ${
                inputMode === mode
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-600 hover:text-slate-950"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="min-w-0">
          {inputMode === "blueprint" ? (
            <div className="grid min-w-0 gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <label
                  htmlFor="headline-project"
                  className="block text-sm font-medium text-slate-700"
                >
                  Project
                </label>
                <select
                  id="headline-project"
                  value={selectedProjectId}
                  onChange={(event) => {
                    setSelectedProjectId(event.target.value);
                    setSelectedBlueprintId("");
                  }}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))
                  ) : (
                    <option value="">No projects found</option>
                  )}
                </select>
              </div>

              <div className="min-w-0">
                <label
                  htmlFor="headline-blueprint"
                  className="block text-sm font-medium text-slate-700"
                >
                  Generated blueprint
                </label>
                <select
                  id="headline-blueprint"
                  value={selectedBlueprintId}
                  onChange={(event) => setSelectedBlueprintId(event.target.value)}
                  disabled={filteredBlueprints.length === 0}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                >
                  {filteredBlueprints.length > 0 ? (
                    filteredBlueprints.map((blueprint) => (
                      <option key={blueprint.id} value={blueprint.id}>
                        {blueprint.title}
                      </option>
                    ))
                  ) : (
                    <option value="">
                      This project does not have a generated blueprint yet.
                    </option>
                  )}
                </select>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-slate-500">
                  Choose the project first, then select the generated blueprint
                  you want to use.
                </p>
                {selectedBlueprint ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Target builder:{" "}
                    {formatBuilder(selectedBlueprint.target_builder)}
                  </p>
                ) : filteredBlueprints.length === 0 ? (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    This project does not have a generated blueprint yet.
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
              Manual input is available when you want headline angles for an
              app that does not have a generated blueprint yet. Add enough
              detail below so the headlines are specific.
            </div>
          )}
        </div>

        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm leading-6 text-indigo-950">
          <p className="font-semibold">Blueprint-first by default</p>
          <p className="mt-1">
            The strongest headlines come from your generated blueprint. Audience,
            outcome, and tone make the angles sharper.
          </p>
        </div>
      </div>

      {inputMode === "manual" ? (
        <div className="mt-6">
          <label
            htmlFor="headline-description"
            className="block text-sm font-medium text-slate-700"
          >
            Manual app description
          </label>
          <textarea
            id="headline-description"
            value={appDescription}
            onChange={(event) => {
              setAppDescription(event.target.value);
              setLocalError(null);
            }}
            maxLength={4000}
            rows={5}
            className="mt-2 w-full min-w-0 max-w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="Describe the app, target user, core workflow, and the result it helps people get in at least 40 characters."
          />
          <p className="mt-2 text-xs text-slate-500">
            {appDescription.length} / 4000
          </p>
          {manualDescriptionState ? (
            <div
              className={`mt-2 rounded-md border p-3 text-sm leading-6 ${
                manualDescriptionState.tone === "error"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              <p>{manualDescriptionState.message}</p>
              <p className="mt-1">{MANUAL_DESCRIPTION_EXAMPLE}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 grid min-w-0 gap-4 lg:grid-cols-2">
        <div>
          <label
            htmlFor="headline-audience"
            className="block text-sm font-medium text-slate-700"
          >
            Target audience
          </label>
          <input
            id="headline-audience"
            value={targetAudience}
            onChange={(event) => setTargetAudience(event.target.value)}
            maxLength={500}
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="Example: coaches, course creators, small agencies"
          />
        </div>

        <div>
          <label
            htmlFor="headline-outcome"
            className="block text-sm font-medium text-slate-700"
          >
            Desired outcome
          </label>
          <input
            id="headline-outcome"
            value={desiredOutcome}
            onChange={(event) => setDesiredOutcome(event.target.value)}
            maxLength={500}
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="Example: track client progress without messy spreadsheets"
          />
        </div>

        <div>
          <label
            htmlFor="headline-offer-type"
            className="block text-sm font-medium text-slate-700"
          >
            Offer type
          </label>
          <select
            id="headline-offer-type"
            value={offerType}
            onChange={(event) =>
              setOfferType(event.target.value as SalesHeadlineOfferType)
            }
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {salesOfferTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="headline-tone"
            className="block text-sm font-medium text-slate-700"
          >
            Tone
          </label>
          <select
            id="headline-tone"
            value={tone}
            onChange={(event) => setTone(event.target.value as SalesHeadlineTone)}
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {salesToneOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="headline-traffic-source"
            className="block text-sm font-medium text-slate-700"
          >
            Traffic source
          </label>
          <select
            id="headline-traffic-source"
            value={trafficSource}
            onChange={(event) =>
              setTrafficSource(event.target.value as SalesHeadlineTrafficSource)
            }
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {salesTrafficSourceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6">
        <label
          htmlFor="headline-extra-context"
          className="block text-sm font-medium text-slate-700"
        >
          Optional extra context
        </label>
        <textarea
          id="headline-extra-context"
          value={extraContext}
          onChange={(event) => setExtraContext(event.target.value)}
          maxLength={1000}
          rows={4}
          className="mt-2 w-full min-w-0 max-w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          placeholder="Add offer details, audience temperature, claims to avoid, or where these headlines will be used."
        />
        <p className="mt-2 text-xs text-slate-500">
          {extraContext.length} / 1000
        </p>
      </div>

      <div className="mt-6 flex justify-stretch sm:justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto"
        >
          {isSubmitting
            ? "Generating sales-page headline angles..."
            : "Generate headline angles"}
        </button>
      </div>
    </section>
  );
}
